<script setup>
import { Swiper, SwiperSlide } from "swiper/vue";
import SwiperCore, { Navigation, EffectCoverflow, Lazy } from "swiper";
import { trackSwiperEvents } from "@/analyticsHelper";

SwiperCore.use([Navigation]);

const props = defineProps({
    instagramStories: {
        type: Array,
        required: false,
        default: () => [],
    },
});

const isImageStory = (story) => story.type === "IMAGE";
const isVideoStory = (story) => story.type === "VIDEO";
</script>
<template>
    <swiper
        :breakpoints="{
            '667': {
                slidesPerView: 2,
            },
            '1024': {
                slidesPerView: 3,
            },
        }"
        :navigation="{
            prevEl: '.slider-prev',
            nextEl: '.slider-next',
        }"
        :slides-per-view="1"
        :loop-additional-slides="4"
        :centered-slides="true"
        :loop="true"
        :modules="[EffectCoverflow, Lazy]"
        :coverflow-effect="{ depth: 90, rotate: 5 }"
        :lazy="{ preloadImages: false, lazy: true, loadingClass: 'invisible' }"
        effect="coverflow"
        data-slide-name="INSTRAGRAM_STORIES"
        @swiper="trackSwiperEvents"
    >
        <swiper-slide
            v-for="(igStory, igStoryIndex) in props.instagramStories"
            :key="igStoryIndex"
            v-slot="{ isActive }"
            class="w-full"
        >
            <section
                v-if="isActive"
                class="block group transition-transform ease-in-out duration-300 shadow-lg w-10/12 mx-auto"
            >
                <section v-if="isActive" class="aspect-[11/13] w-full flex flex-col">
                    <img
                        v-if="isImageStory(igStory)"
                        class="w-full h-full object-cover"
                        :src="igStory.resource_url"
                        :alt="igStory.caption"
                    />
                    <video
                        v-else-if="isVideoStory(igStory)"
                        class="w-full aspect-[11/13] object-cover"
                        muted
                        loop
                        autoplay
                    >
                        <source :src="igStory.resource_url" type="video/mp4" />
                    </video>

                    <div class="bg-white flex w-full flex py-4 px-2">
                        <div class="flex bg-gray-100 rounded-full h-12 w-12 mr-2 shrink-0">
                            <svg
                                class="h-8 w-8 m-auto"
                                viewBox="0 0 63 62"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M31.4486 0.858418C13.2168 2.01995 0.0492528 15.9731 0.973178 32.9334C1.79894 48.1836 14.8105 62.9802 32.88 61.5776C49.0413 60.3297 64.0946 47.8629 62.513 29.7234C61.3987 12.3534 47.8898 -0.204611 31.4523 0.844744L31.4486 0.858418ZM14.6098 40.7526C13.4193 38.0106 13.7924 37.1369 16.6372 36.1642C26.5463 32.7872 36.4654 29.4276 46.3858 26.0903C49.0647 25.1903 50.0073 25.743 50.6791 28.4266C53.6072 40.0382 45.2548 51.2114 33.0864 51.9552C25.123 52.4421 17.7354 47.9663 14.6098 40.7526V40.7526ZM12.9977 23.0004C12.7499 20.2913 14.8996 17.8147 17.6573 17.6312C20.3345 17.4481 22.6642 19.4633 22.8832 22.1427C23.1086 24.8531 20.9726 27.3334 18.2336 27.5291C15.5974 27.7232 13.2391 25.6785 12.989 23.0054L12.9977 23.0004ZM37.8139 20.8433C35.0475 21.0317 32.8237 19.1402 32.6106 16.4111C32.3908 13.5704 34.3627 11.2657 37.201 11.0819C39.9451 10.8948 42.1702 12.8087 42.3919 15.5327C42.6254 18.3771 40.6609 20.6545 37.8189 20.8519L37.8139 20.8433Z"
                                    class="fill-orange"
                                />
                            </svg>
                        </div>
                        <p class="text-sm line-clamp-3" v-text="igStory.caption"></p>
                    </div>
                </section>
            </section>
            <section v-else class="block group transition-transform ease-in-out duration-300 shadow-lg mx-auto">
                <section class="aspect-[11/13] w-full">
                    <div class="w-full h-full bg-black overflow-hidden relative">
                        <img
                            v-if="isImageStory(igStory)"
                            class="swiper-lazy w-full h-full object-cover"
                            :data-src="igStory.resource_url"
                            alt="IG image preview"
                        />

                        <img
                            v-else-if="isVideoStory(igStory)"
                            class="swiper-lazy w-full h-full object-cover"
                            :data-src="igStory.thumbnail_url"
                            alt="IG image preview"
                        />

                        <div class="swiper-lazy-preloader"></div>
                    </div>
                </section>
            </section>
        </swiper-slide>

        <section class="flex justify-center pt-5 space-x-3">
            <button class="slider-prev p-3 hover:text-teal">
                <i class="fa-regular fa-long-arrow-left text-2xl"></i>
            </button>
            <button class="slider-next p-3 hover:text-teal">
                <i class="fa-regular fa-long-arrow-right text-2xl"></i>
            </button>
        </section>

        <input type="hidden" class="swiper-slide swiper-wrapper" />
    </swiper>
</template>

<style>
.swiper-wrapper {
    height: unset;
}

.swiper-slide {
    height: unset;
}
</style>
